import { useAuth } from "@/hooks/useAuth";
import { useConversations } from "@/hooks/queries/useMessages";
import { useDefaultConversation } from "@/hooks/queries/useDefaultConversation";
import { formatImageUrl, getInitials } from "@/utils/formatters";
import { formatDistanceToNow } from "date-fns";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface ConversationListProps {
  selectedConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
}

const ConversationList = ({
  selectedConversationId,
  onSelectConversation,
}: ConversationListProps) => {
  const { t } = useTranslation();
  const { data: conversations, isLoading, error } = useConversations();
  const { data: defaultPartner } = useDefaultConversation();


  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {t("messaging.loadingConversations", "Loading conversations...")}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t("messaging.errorTitle", "Error")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("messaging.errorLoadingConversations", "Error loading conversations")}
          </p>
        </div>
      </div>
    );
  }

  // Show default partner even if no conversations exist
  if (!conversations || conversations.length === 0) {
    console.log("🔄 [CONVERSATION_LIST] No conversations, showing default partner:", {
      defaultPartner: defaultPartner ? {
        id: defaultPartner.id,
        displayName: defaultPartner.displayName,
        type: defaultPartner.type
      } : null,
      selectedConversationId
    });
    
    if (defaultPartner) {
      return (
        <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("messaging.conversations", "Conversations")}
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <motion.div
              whileHover={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}
              className={`p-4 cursor-pointer transition-colors ${
                selectedConversationId === defaultPartner.id
                  ? "bg-primary-50 border-r-2 border-primary-500 dark:bg-primary-900/20"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
              onClick={() => {
                console.log("📝 [CONVERSATION_LIST] Selecting default partner:", defaultPartner.id);
                onSelectConversation(defaultPartner.id);
              }}
            >
              <div className="flex items-start space-x-3">
                {/* Profile Image */}
                <div className="flex-shrink-0">
                  {defaultPartner.profilePhoto ? (
                    <img
                      src={formatImageUrl(defaultPartner.profilePhoto)}
                      alt={defaultPartner.displayName}
                      className="h-12 w-12 rounded-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}
                  
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 ${
                      defaultPartner.profilePhoto ? "hidden" : ""
                    }`}
                  >
                    <span className="text-sm font-medium">
                      {getInitials(defaultPartner.displayName)}
                    </span>
                  </div>
                </div>

                {/* Conversation Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {defaultPartner.displayName}
                    </h3>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {t("messaging.defaultChat", "Default")}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate mt-1">
                    {t("messaging.startConversation", "Start a conversation")}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t("messaging.noConversations", "No conversations yet")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("messaging.startFirstConversation", "Start your first conversation!")}
          </p>
        </div>
      </div>
    );
  }

  console.log("🔄 [CONVERSATION_LIST] Rendering conversations:", {
    conversationsCount: conversations.length,
    conversations: conversations.map(conv => ({
      id: conv.id,
      chatWithId: conv.chatWith.id,
      chatWithName: conv.chatWith.displayName,
      chatWithType: conv.chatWith.type
    })),
    selectedConversationId
  });

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
      <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 shadow-sm">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
          {t("messaging.conversations", "Conversations")}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {conversations.length} {conversations.length === 1 ? 'conversation' : 'conversations'}
        </p>
      </div>
      
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {conversations.map((conversation) => (
          <motion.div
            key={conversation.id}
            whileTap={{ scale: 0.98 }}
            className={`p-3 sm:p-4 cursor-pointer transition-all active:bg-primary-100 dark:active:bg-primary-900/30 touch-manipulation ${
              selectedConversationId === conversation.id
                ? "bg-primary-50 border-l-4 border-primary-500 dark:bg-primary-900/20 shadow-sm"
                : "hover:bg-gray-50 dark:hover:bg-gray-800 border-l-4 border-transparent"
            }`}
            onClick={() => {
              console.log("📝 [CONVERSATION_LIST] Selecting conversation:", {
                conversationId: conversation.id,
                chatWithId: conversation.chatWith.id,
                chatWithName: conversation.chatWith.displayName,
                chatWithType: conversation.chatWith.type
              });
              onSelectConversation(conversation.id);
            }}
          >
            <div className="flex items-start space-x-3">
              {/* Profile Image */}
              <div className="flex-shrink-0 relative">
                {conversation.chatWith.profilePhoto ? (
                  <img
                    src={formatImageUrl(conversation.chatWith.profilePhoto)}
                    alt={conversation.chatWith.displayName}
                    className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover ring-2 ring-gray-200 dark:ring-gray-700"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      target.nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                ) : null}
                
                <div
                  className={`flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 dark:from-primary-900 dark:to-primary-800 dark:text-primary-300 ring-2 ring-gray-200 dark:ring-gray-700 ${
                    conversation.chatWith.profilePhoto ? "hidden" : ""
                  }`}
                >
                  <span className="text-base sm:text-lg font-bold">
                    {getInitials(conversation.chatWith.displayName)}
                  </span>
                </div>
                
                {conversation.unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 h-5 w-5 sm:h-6 sm:w-6 bg-red-500 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800 shadow-lg">
                    <span className="text-[10px] sm:text-xs font-bold text-white">
                      {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                    </span>
                  </div>
                )}
              </div>

              {/* Conversation Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate">
                    {conversation.chatWith.displayName}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0">
                    {formatDistanceToNow(new Date(conversation.createdAt), {
                      addSuffix: true,
                    })}
                  </p>
                </div>
                
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate line-clamp-2">
                  {conversation.lastMessage?.text || t("messaging.noMessages", "No messages yet")}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ConversationList;
